import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import { useDropzone } from "react-dropzone";
import { Loader2, Upload, Trash2, Download, Square, CheckSquare, ZoomIn, ZoomOut } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { PDFDocument, rgb } from "pdf-lib";
import { getDocument, GlobalWorkerOptions, PDFDocumentProxy } from "pdfjs-dist";

// Configure pdf.js worker for Vite/ESM
const pdfWorkerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();
GlobalWorkerOptions.workerSrc = pdfWorkerSrc;

type MaskBox = {
  id: string;
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
};

type RenderedPage = {
  pageNumber: number;
  url: string;
  width: number;
  height: number;
};

const SCALE_STEP = 0.1;
const MIN_SCALE = 0.6;
const MAX_SCALE = 2.0;

const PdfMaskerPage: React.FC = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [pdfBytes, setPdfBytes] = useState<Uint8Array | null>(null);
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null);
  const [pages, setPages] = useState<RenderedPage[]>([]);
  const [boxes, setBoxes] = useState<MaskBox[]>([]);
  const [draftBox, setDraftBox] = useState<MaskBox | null>(null);
  const [selectedPages, setSelectedPages] = useState<number[]>([]);
  const [scale, setScale] = useState(1.1);
  const containerRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const lastBoxRef = useRef<MaskBox | null>(null);
  const [drawingPage, setDrawingPage] = useState<number | null>(null);

  const onDrop = useCallback(async (accepted: File[]) => {
    if (!accepted.length) return;
    const file = accepted[0];

    if (!file.type.includes("pdf")) {
      toast({ title: "Only PDFs are supported", variant: "destructive" });
      return;
    }

    try {
      setLoading(true);
      setBoxes([]);
      setDraftBox(null);

      const buffer = await file.arrayBuffer();
      // Clone bytes so pdf.js cannot detach the same buffer we use for export
      const bytes = new Uint8Array(buffer);
      setPdfBytes(bytes);

      const doc = await getDocument({ data: bytes.slice() }).promise;
      setPdfDoc(doc);

      const rendered: RenderedPage[] = [];
      for (let i = 1; i <= doc.numPages; i++) {
        const page = await doc.getPage(i);
        const viewport = page.getViewport({ scale: scale * 1.2 });
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({
          canvasContext: context!,
          viewport,
        }).promise;

        rendered.push({
          pageNumber: i,
          url: canvas.toDataURL("image/png"),
          width: viewport.width,
          height: viewport.height,
        });
      }

      setPages(rendered);
      setSelectedPages(rendered.map((p) => p.pageNumber));
      toast({ title: "PDF loaded", description: `${file.name} ready for masking.` });
    } catch (err) {
      console.error(err);
      toast({
        title: "Failed to load PDF",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [scale, toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "application/pdf": [".pdf"] },
    multiple: false,
    onDrop,
  });

  const togglePageSelection = (pageNumber: number) => {
    setSelectedPages((prev) =>
      prev.includes(pageNumber) ? prev.filter((p) => p !== pageNumber) : [...prev, pageNumber]
    );
  };

  const handleStartDraw = (pageNumber: number, event: React.MouseEvent<HTMLDivElement>) => {
    const container = containerRefs.current[pageNumber];
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const startX = event.clientX - rect.left;
    const startY = event.clientY - rect.top;
    setDraftBox({
      id: uuid(),
      pageNumber,
      x: startX,
      y: startY,
      width: 0,
      height: 0,
    });
    setDrawingPage(pageNumber);
  };

  const handleMouseMove = (pageNumber: number, event: React.MouseEvent<HTMLDivElement>) => {
    if (!draftBox || drawingPage !== pageNumber) return;
    const container = containerRefs.current[pageNumber];
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const currentX = event.clientX - rect.left;
    const currentY = event.clientY - rect.top;
    const width = currentX - draftBox.x;
    const height = currentY - draftBox.y;
    setDraftBox({
      ...draftBox,
      width,
      height,
    });
  };

  const handleFinishDraw = (pageNumber: number, event: React.MouseEvent<HTMLDivElement>) => {
    if (!draftBox || drawingPage !== pageNumber) return;
    const container = containerRefs.current[pageNumber];
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const endX = event.clientX - rect.left;
    const endY = event.clientY - rect.top;
    const width = endX - draftBox.x;
    const height = endY - draftBox.y;
    const normalizedBox: MaskBox = {
      ...draftBox,
      width: Math.abs(width),
      height: Math.abs(height),
      x: width < 0 ? draftBox.x + width : draftBox.x,
      y: height < 0 ? draftBox.y + height : draftBox.y,
    };

    // Ignore tiny accidental drags
    if (normalizedBox.width < 10 || normalizedBox.height < 10) {
      setDraftBox(null);
      setDrawingPage(null);
      return;
    }

    setBoxes((prev) => {
      const next = [...prev, normalizedBox];
      lastBoxRef.current = normalizedBox;
      return next;
    });
    setDraftBox(null);
    setDrawingPage(null);
  };

  const undoLast = useCallback(() => {
    setBoxes((prev) => {
      if (!prev.length) return prev;
      const next = prev.slice(0, -1);
      lastBoxRef.current = next[next.length - 1] ?? null;
      return next;
    });
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isUndo = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z";
      if (isUndo) {
        e.preventDefault();
        undoLast();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [undoLast]);

  const clearPageBoxes = (pageNumber: number) => {
    setBoxes((prev) => prev.filter((box) => box.pageNumber !== pageNumber));
  };

  const handleDownload = async () => {
    if (!pdfBytes || !pdfDoc) {
      toast({ title: "Upload a PDF first", variant: "destructive" });
      return;
    }
    if (!selectedPages.length) {
      toast({ title: "Select at least one page", variant: "destructive" });
      return;
    }

    try {
      setLoading(true);
      // Use a fresh copy to avoid "detached ArrayBuffer" errors
      const sourceDoc = await PDFDocument.load(pdfBytes.slice());
      const targetDoc = await PDFDocument.create();

      for (const pageNumber of selectedPages.sort((a, b) => a - b)) {
        const [copiedPage] = await targetDoc.copyPages(sourceDoc, [pageNumber - 1]);
        const renderMeta = pages.find((p) => p.pageNumber === pageNumber);
        targetDoc.addPage(copiedPage);

        if (renderMeta) {
          const pageBoxes = boxes.filter((b) => b.pageNumber === pageNumber);
          const pdfWidth = copiedPage.getWidth();
          const pdfHeight = copiedPage.getHeight();
          const scaleX = pdfWidth / renderMeta.width;
          const scaleY = pdfHeight / renderMeta.height;

          pageBoxes.forEach((box) => {
            const x = box.x * scaleX;
            const width = box.width * scaleX;
            const y = pdfHeight - (box.y + box.height) * scaleY;
            const height = box.height * scaleY;

            // Draw multiple overlapping filled rectangles to ensure complete coverage
            // This prevents any text from being selectable underneath
            // The multiple layers ensure the graphics completely cover the text layer
            
            // Layer 1: Main rectangle
            copiedPage.drawRectangle({
              x,
              y,
              width,
              height,
              color: rgb(0, 0, 0),
              borderColor: rgb(0, 0, 0),
              opacity: 1,
            });

            // Layer 2: Slightly larger rectangle to ensure edge coverage
            copiedPage.drawRectangle({
              x: x - 0.5,
              y: y - 0.5,
              width: width + 1,
              height: height + 1,
              color: rgb(0, 0, 0),
              borderColor: rgb(0, 0, 0),
              opacity: 1,
            });

            // Layer 3: Exact dimensions again for solid fill
            copiedPage.drawRectangle({
              x,
              y,
              width,
              height,
              color: rgb(0, 0, 0),
              borderColor: rgb(0, 0, 0),
              opacity: 1,
            });
          });
        }
      }

      const exportedBytes = await targetDoc.save();
      const blob = new Blob([exportedBytes], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "masked.pdf";
      link.click();
      toast({ title: "Download ready", description: "Masked PDF downloaded." });
    } catch (err) {
      console.error(err);
      toast({
        title: "Failed to export PDF",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const zoomIn = () => setScale((s) => Math.min(MAX_SCALE, parseFloat((s + SCALE_STEP).toFixed(2))));
  const zoomOut = () => setScale((s) => Math.max(MIN_SCALE, parseFloat((s - SCALE_STEP).toFixed(2))));

  useEffect(() => {
    // re-render on scale change if a PDF is already loaded
    const rerender = async () => {
      if (!pdfBytes) return;
      setLoading(true);
      try {
        const doc = await getDocument({ data: pdfBytes.slice() }).promise;
        setPdfDoc(doc);
        const rendered: RenderedPage[] = [];
        for (let i = 1; i <= doc.numPages; i++) {
          const page = await doc.getPage(i);
          const viewport = page.getViewport({ scale: scale * 1.2 });
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          await page.render({
            canvasContext: context!,
            viewport,
          }).promise;
          rendered.push({
            pageNumber: i,
            url: canvas.toDataURL("image/png"),
            width: viewport.width,
            height: viewport.height,
          });
        }
        setPages(rendered);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    rerender();
  }, [scale, pdfBytes]);

  const activeBoxesByPage = useMemo(() => {
    return pages.reduce<Record<number, MaskBox[]>>((acc, page) => {
      acc[page.pageNumber] = boxes.filter((b) => b.pageNumber === page.pageNumber);
      return acc;
    }, {});
  }, [boxes, pages]);

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">PDF Masker</h1>
          <p className="text-gray-600">Upload a PDF, drag to add black boxes, and download selected pages.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={zoomOut} disabled={scale <= MIN_SCALE}>
            <ZoomOut className="mr-2 h-4 w-4" />
            Zoom out
          </Button>
          <Button variant="outline" onClick={zoomIn} disabled={scale >= MAX_SCALE}>
            <ZoomIn className="mr-2 h-4 w-4" />
            Zoom in
          </Button>
          <Button onClick={handleDownload} disabled={loading || !pdfBytes}>
            <Download className="mr-2 h-4 w-4" />
            Download masked PDF
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Upload PDF</CardTitle>
          <CardDescription>Drag a PDF here or click to browse. We’ll render all pages.</CardDescription>
        </CardHeader>
        <CardContent>
          <div
            {...getRootProps()}
            className={cn(
              "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition",
              isDragActive ? "border-primary bg-primary/5" : "border-gray-200 hover:border-primary"
            )}
          >
            <input {...getInputProps()} />
            <Upload className="mx-auto h-8 w-8 text-gray-500 mb-2" />
            <p className="font-medium">Drop PDF here or click to upload</p>
            <p className="text-sm text-gray-500">Single PDF, will render all pages for masking.</p>
          </div>
        </CardContent>
      </Card>

      {!pdfBytes && (
        <Alert>
          <AlertTitle>No PDF loaded yet</AlertTitle>
          <AlertDescription>Upload a PDF to start masking.</AlertDescription>
        </Alert>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Loader2 className="h-4 w-4 animate-spin" />
          Processing PDF...
        </div>
      )}

      {pdfBytes && (
        <div className="grid grid-cols-1 lg:grid-cols-[2fr,1fr] gap-4">
          <Card className="overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Pages</CardTitle>
                <CardDescription>Double-click to start, move the mouse, single-click to finish a mask. Use the trash to clear a page.</CardDescription>
              </div>
              <Badge variant="secondary">{pages.length} pages</Badge>
            </CardHeader>
            <Separator />
            <CardContent className="p-0">
              <ScrollArea className="h-[70vh]">
                <div className="space-y-6 p-4">
                  {pages.map((page) => (
                    <div key={page.pageNumber} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={selectedPages.includes(page.pageNumber)}
                            onCheckedChange={() => togglePageSelection(page.pageNumber)}
                            id={`page-${page.pageNumber}`}
                          />
                          <label htmlFor={`page-${page.pageNumber}`} className="font-medium">
                            Page {page.pageNumber}
                          </label>
                          <Badge variant="outline">
                            {activeBoxesByPage[page.pageNumber]?.length || 0} masks
                          </Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => clearPageBoxes(page.pageNumber)}
                          disabled={!activeBoxesByPage[page.pageNumber]?.length}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Clear
                        </Button>
                      </div>
                      <div
                        ref={(el) => (containerRefs.current[page.pageNumber] = el)}
                        className="relative border rounded-lg overflow-hidden bg-white shadow-sm touch-none select-none"
                        style={{ width: page.width, height: page.height }}
                        onDoubleClick={(e) => handleStartDraw(page.pageNumber, e)}
                        onMouseMove={(e) => handleMouseMove(page.pageNumber, e)}
                        onClick={(e) => handleFinishDraw(page.pageNumber, e)}
                      >
                        <img src={page.url} alt={`Page ${page.pageNumber}`} className="block w-full h-full object-contain" />
                        {activeBoxesByPage[page.pageNumber]?.map((box) => (
                          <div
                            key={box.id}
                            className="absolute bg-black bg-opacity-80 border border-black/80"
                            style={{
                              left: box.x,
                              top: box.y,
                              width: box.width,
                              height: box.height,
                            }}
                            title={`Mask ${box.id}`}
                          />
                        ))}
                        {draftBox && draftBox.pageNumber === page.pageNumber && (
                          <div
                            className="absolute bg-black bg-opacity-40 border border-dashed border-black"
                            style={{
                              left: draftBox.x,
                              top: draftBox.y,
                              width: draftBox.width,
                              height: draftBox.height,
                            }}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Summary</CardTitle>
              <CardDescription>Pick pages to export and manage masks.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Square className="h-4 w-4" />
                <span className="text-sm text-gray-600">
                  Double-click to set start, move, single-click to place a mask. Clear removes masks on that page.
                </span>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Pages selected</span>
                  <Badge>{selectedPages.length}</Badge>
                </div>
                <div className="flex flex-wrap gap-2">
                  {pages.map((p) => (
                    <Button
                      key={p.pageNumber}
                      variant={selectedPages.includes(p.pageNumber) ? "default" : "outline"}
                      size="sm"
                      onClick={() => togglePageSelection(p.pageNumber)}
                      className="gap-1"
                    >
                      {selectedPages.includes(p.pageNumber) ? (
                        <CheckSquare className="h-4 w-4" />
                      ) : (
                        <Square className="h-4 w-4" />
                      )}
                      {p.pageNumber}
                    </Button>
                  ))}
                </div>
              </div>

              <Separator />

              <div className="space-y-2">
                <div className="font-medium">Masks</div>
                {boxes.length === 0 ? (
                  <p className="text-sm text-gray-500">No masks added yet.</p>
                ) : (
                  <ScrollArea className="h-48">
                    <div className="space-y-2 pr-2">
                      {boxes.map((box) => (
                        <div
                          key={box.id}
                          className="flex items-center justify-between rounded border p-2 text-sm"
                        >
                          <div>
                            <div className="font-medium">Page {box.pageNumber}</div>
                            <div className="text-xs text-gray-500">
                              x:{Math.round(box.x)} y:{Math.round(box.y)} w:{Math.round(box.width)} h:{Math.round(box.height)}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setBoxes((prev) => prev.filter((b) => b.id !== box.id))}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </div>

              <Separator />

              <Button onClick={handleDownload} className="w-full" disabled={loading || !pdfBytes}>
                <Download className="mr-2 h-4 w-4" />
                Download selected pages
              </Button>
              <p className="text-xs text-gray-500">
                We generate a new PDF with your selected pages and applied black boxes.
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PdfMaskerPage;

